/**
 * Downloads for reports (v7 CORE-1 §7): CSV and XLSX of the same columns, in the screen's order, with the totals row.
 * XLSX is written directly (one sheet, inline strings, numbers as numbers) and zipped with fflate — no spreadsheet library.
 * readSheet reads an uploaded XLSX (its first sheet) or CSV back into rows keyed by the header (Import Centre, CORE-2).
 */
import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate';

export interface SheetColumn { key: string; label: string; kind?: string }
type Row = Record<string, unknown>;

const cellText = (v: unknown): string => (v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));

function download(name: string, data: BlobPart, type: string) {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Paise columns go out as rupees (a spreadsheet adds them as money). */
const value = (c: SheetColumn, v: unknown): unknown => (c.kind === 'paise' && typeof v === 'number' ? v / 100 : v);

export function downloadCsv(name: string, columns: SheetColumn[], rows: Row[], totals?: Row) {
  const esc = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  const lines = [columns.map((c) => esc(c.label)).join(',')];
  for (const r of rows) lines.push(columns.map((c) => esc(cellText(value(c, r[c.key])))).join(','));
  if (totals && Object.keys(totals).length) lines.push(columns.map((c, i) => esc(i === 0 ? 'Total' : cellText(value(c, totals[c.key])))).join(','));
  // A byte-order mark so Excel reads ₹ and Hindi correctly.
  download(`${name}.csv`, '﻿' + lines.join('\r\n'), 'text/csv;charset=utf-8');
}

const xml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const colName = (i: number): string => { let s = ''; i += 1; while (i > 0) { const m = (i - 1) % 26; s = String.fromCharCode(65 + m) + s; i = Math.floor((i - 1) / 26); } return s; };

export function buildXlsx(sheetName: string, columns: SheetColumn[], rows: Row[], totals?: Row): Uint8Array {
  const all: unknown[][] = [columns.map((c) => c.label), ...rows.map((r) => columns.map((c) => value(c, r[c.key])))];
  if (totals && Object.keys(totals).length) all.push(columns.map((c, i) => (i === 0 ? 'Total' : value(c, totals[c.key]))));
  const sheetRows = all.map((row, ri) => `<row r="${ri + 1}">${row.map((v, ci) => {
    const ref = `${colName(ci)}${ri + 1}`;
    if (typeof v === 'number' && Number.isFinite(v)) return `<c r="${ref}"><v>${v}</v></c>`;
    const t = cellText(v);
    return t === '' ? '' : `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${xml(t)}</t></is></c>`;
  }).join('')}</row>`).join('');
  const files: Record<string, Uint8Array> = {
    '[Content_Types].xml': strToU8('<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>'),
    '_rels/.rels': strToU8('<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>'),
    'xl/workbook.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${xml(sheetName.slice(0, 31))}" sheetId="1" r:id="rId1"/></sheets></workbook>`),
    'xl/_rels/workbook.xml.rels': strToU8('<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>'),
    'xl/worksheets/sheet1.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${sheetRows}</sheetData></worksheet>`),
  };
  return zipSync(files);
}

export function downloadXlsx(name: string, columns: SheetColumn[], rows: Row[], totals?: Row) {
  download(`${name}.xlsx`, buildXlsx(name, columns, rows, totals).slice().buffer as ArrayBuffer, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
}

// ── Reading an upload (Import Centre) ───────────────────────────────────────────────────────────────────────────
/** The header as a key: "Start date" → start_date. */
export const headerKey = (h: string) => h.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

function parseCsv(text: string): string[][] {
  const rows: string[][] = []; let row: string[] = []; let cell = ''; let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i += 1; } else if (ch === '"') quoted = false; else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') { if (ch === '\r' && text[i + 1] === '\n') i += 1; row.push(cell); rows.push(row); row = []; cell = ''; }
    else cell += ch;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return rows;
}

const colIndex = (ref: string): number => { let n = 0; for (const c of ref.replace(/[0-9]/g, '')) n = n * 26 + (c.charCodeAt(0) - 64); return n - 1; };
const unxml = (s: string) => s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&amp;/g, '&');

function parseXlsx(bytes: Uint8Array): string[][] {
  const files = unzipSync(bytes);
  const shared: string[] = [];
  const ss = files['xl/sharedStrings.xml'];
  if (ss) for (const m of strFromU8(ss).matchAll(/<si>([\s\S]*?)<\/si>/g)) shared.push(unxml([...m[1]!.matchAll(/<t[^>]*>([\s\S]*?)<\/t>/g)].map((t) => t[1]).join('')));
  const sheetPath = Object.keys(files).filter((k) => /^xl\/worksheets\/sheet\d+\.xml$/.test(k)).sort()[0];
  if (!sheetPath) throw new Error('This file has no sheet');
  const out: string[][] = [];
  for (const r of strFromU8(files[sheetPath]!).matchAll(/<row[^>]*>([\s\S]*?)<\/row>/g)) {
    const row: string[] = [];
    for (const c of r[1]!.matchAll(/<c ([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attrs = c[1]!; const inner = c[2] ?? '';
      const ref = /r="([A-Z]+)\d+"/.exec(attrs)?.[1]; const type = /t="([^"]+)"/.exec(attrs)?.[1];
      const v = /<v>([\s\S]*?)<\/v>/.exec(inner)?.[1];
      const text = type === 's' ? (shared[Number(v)] ?? '') : type === 'inlineStr' ? unxml([...inner.matchAll(/<t[^>]*>([\s\S]*?)<\/t>/g)].map((t) => t[1]).join('')) : unxml(v ?? '');
      row[ref ? colIndex(ref) : row.length] = text;
    }
    out.push(Array.from(row, (x) => x ?? ''));
  }
  return out;
}

/** An uploaded XLSX or CSV as rows keyed by the header row; empty rows are left out. */
export async function readSheet(file: File): Promise<Record<string, string>[]> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const grid = /\.xlsx$/i.test(file.name) ? parseXlsx(bytes) : parseCsv(new TextDecoder('utf-8').decode(bytes).replace(/^\uFEFF/, ''));
  const [head, ...body] = grid;
  if (!head) return [];
  const keys = head.map(headerKey);
  return body.filter((r) => r.some((c) => (c ?? '').trim() !== ''))
    .map((r) => Object.fromEntries(keys.map((k, i) => [k, (r[i] ?? '').trim()])));
}
